import { DEV } from 'cc/env';
import { CCInteger, CCString, director, error, instantiate, Node, Prefab, _decorator } from 'cc';
import GameDependency from './System/GameDependency';

const { ccclass, property } = _decorator;

enum EventType {
    BEFORE_SCENE_CHANGED = 'BEFORE_SCENE_CHANGED',
    AFTER_SCENE_CHANGED = 'AFTER_SCENE_CHANGED',
    NODE_CREATED = 'NODE_CREATED',
}

@ccclass('SceneData')
class SceneData {
    @property(Prefab)
    public readonly prefab: Prefab = null;

    @property(CCInteger)
    public readonly renderOrder: number = 0;

    @property
    private _name: string = '';

    @property
    public get name() {
        return this._name || this.prefab?.name || '';
    }
    public set name(value: string) {
        if (this._name === value) {
            return;
        }
        this._name = value;
    }
}

// HACK do not change the ccclass name for compatibility
@ccclass('CrossSceneNodeData')
class NodeData {
    @property(Prefab)
    public readonly prefab: Prefab;

    @property(CCString)
    public readonly path: string;

    @property(CCInteger)
    public readonly renderOrder: number = 0;

    @property({ tooltip: DEV && 'If true, the node will be attached to current scene when scene changed' })
    public readonly crossScene: boolean = true;

    @property
    private _name: string = '';

    public crossSceneCheck: (newSceneName: string) => boolean = null;

    @property
    public get name() {
        return this._name || this.prefab?.name || '';
    }
    public set name(value: string) {
        if (this._name === value) {
            return;
        }
        this._name = value;
    }
}

@ccclass('SceneManager')
export default class SceneManager extends GameDependency {
    public static readonly EVENT_TYPE = EventType;

    @property([SceneData])
    private readonly sceneDataList: SceneData[] = [];

    @property({ type: [NodeData], formerlySerializedAs: 'crossSceneNodeDataList' })
    private readonly nodeDataList: NodeData[] = [];

    @property(Node)
    private readonly root: Node = null;

    private _sceneMap: { [sceneName: string]: Node } = {};
    private _nodeMap: { [path: string]: Node } = {};
    private _currentSceneName = '';

    public get currentSceneName() {
        return this._currentSceneName;
    }

    public get currentScene() {
        return this._sceneMap[this._currentSceneName];
    }

    public get allNodes() {
        return Object.values(this._nodeMap);
    }

    public get allCrossSceneNodes() {
        return this.crossSceneNodeDataList.map((data) => this._nodeMap[data.name]).filter(Boolean);
    }

    private get crossSceneNodeDataList() {
        return this.nodeDataList.filter((data) => data.crossScene || data.crossSceneCheck?.(this.currentSceneName));
    }

    public onLoad() {
        if (this.game.scene) {
            this.destroy();
            error('No duplicate SceneManager');
            return;
        }
        this.game.scene = this;
    }

    public onDestroy() {
        this.game.scene = null;
    }

    public enter(sceneName: string) {
        const sceneData = this.sceneDataList.find((data) => data.name === sceneName);
        if (!sceneData) {
            error(`Can't find scene: ${sceneName}`);
            return;
        }
        if (this._currentSceneName === sceneName) {
            return;
        }
        this._currentSceneName = sceneName;
        this.node.emit(EventType.BEFORE_SCENE_CHANGED);
        if (!this._sceneMap[sceneName]) {
            let scene: Node = null;
            try {
                scene = instantiate(sceneData.prefab);
                this._sceneMap[sceneName] = scene;
                scene.parent = this.root;
                scene.name = sceneName;
                scene.zIndex = sceneData.renderOrder;
            } catch (err) {
                this._sceneMap[sceneName] = scene;
                throw err;
            }
        }
        Object.values(this._sceneMap).forEach((scene) => {
            if (sceneName === scene.name) {
                scene.setParent(this.root);
                this._appendCrossSceneNodeToScene(scene);
            } else {
                scene.setParent(null);
            }
        });
        this.node.emit(EventType.AFTER_SCENE_CHANGED);
    }

    public getScene(name: string) {
        return this._sceneMap[name];
    }

    public isNodeExist(name: string) {
        return !!this._nodeMap[name];
    }

    /**
     * try to get node by name, if node not exist, return null
     * @param name target node name
     */
    public tryGetNode(name: string): Node {
        const nodeData = this.nodeDataList.find((data) => data.name === name);
        if (!nodeData) {
            error(`Can't find node: ${name}`);
            return null;
        }
        return this._nodeMap[nodeData.name] ?? null;
    }

    /**
     * get node by name, if node not exist, create it
     * @param name target node name
     */
    public getNode(name: string): Node {
        const nodeData = this.nodeDataList.find((data) => data.name === name);
        if (!nodeData) {
            error(`Can't find node: ${name}`);
            return null;
        }
        return this._spawnNode(nodeData);
    }

    public customizeCrossSceneCheck(name: string, crossSceneCheck: (newSceneName: string) => boolean) {
        const nodeData = this.nodeDataList.find((data) => data.name === name);
        if (!nodeData) {
            error(`Can't find node: ${name}`);
            return;
        }
        nodeData.crossSceneCheck = crossSceneCheck;
    }

    private _appendCrossSceneNodeToScene(sceneNode: Node) {
        this.crossSceneNodeDataList.forEach((data) => {
            const crossSceneNode = this._nodeMap[data.name];
            if (!crossSceneNode) {
                return;
            }
            const root = sceneNode.getChildByPath(data.path);
            if (!root) {
                return;
            }
            root.addChild(crossSceneNode);
        });
    }

    private _spawnNode(data: NodeData) {
        if (this._nodeMap[data.name]) {
            return this._nodeMap[data.name];
        }
        const node = instantiate(data.prefab);
        node.zIndex = data.renderOrder;
        (this.currentScene ?? director.getScene()).getChildByPath(data.path).addChild(node);
        this._nodeMap[data.name] = node;
        this.node.emit(EventType.NODE_CREATED, node);
        return node;
    }
}
